import { useCallback, useEffect, useState } from 'react'
import { supabase, readableError } from '../../lib/supabase'
import { Shell } from '../../components/Shell'
import { Player, Spinner } from '../../components/ui'
import { useSession } from '../../lib/session'

/**
 * What the three moderators have decided, and what to do about it.
 *
 * Beseter checks a song in three places and none of them could see each other:
 * the browser scores how song-like the audio is (lib/musicality.js), a
 * Cloudflare Worker asks Gemini about the lyrics (moderator/src/index.ts), and
 * an edge function asks Gemini to listen to the recording
 * (supabase/functions/moderate-audio). All three write to content_flags. This
 * reads the moderation_dashboard view, which folds them into one 0-100 score.
 *
 * Neither the score nor the verdict text is produced here. The score is
 * migration 20's arithmetic and the explanation is the model's own sentence,
 * stored when it ruled. This screen shows them and sends back a decision
 * through moderation_decide (migration 21) — it does not form opinions of its
 * own, because a second opinion computed in a browser is one nobody can audit.
 */

/** Where the finding came from, in words rather than column values. */
const SOURCE = {
  dsp_not_music: 'In-browser analysis',
  text_filter: 'Gemini · read the lyrics',
  gemini_audio: 'Gemini · listened to the recording',
  user_report: 'Reported by a person',
  human_review: 'You',
}

const SEVERITY = { info: 'Note', review: 'Worth a look', high: 'Serious' }
const STATUS = {
  open: 'Open', reviewing: 'With a human', upheld: 'Confirmed', dismissed: 'Cleared',
}

/** copyright_flags has its own source/status columns — not the same values as content_flags above. */
const CSOURCE = {
  title_match: 'Title matches the watchlist',
  lyric_match_watchlist: 'Lyrics match the watchlist',
  lyric_match_platform: 'Lyrics match another song here',
  declaration_conflict: 'Conflicts with a declaration',
  audio_fingerprint: 'Audio fingerprint match',
  reported: 'Reported by a person',
}
const CSEVERITY = { notice: 'Note', review: 'Worth a look', high: 'Serious' }
const CSTATUS = { open: 'Open', cleared: 'Cleared', conflict: 'Confirmed conflict' }

const ago = (iso) => {
  if (!iso) return null
  const h = Math.floor((Date.now() - new Date(iso)) / 3.6e6)
  if (h < 1) return 'just now'
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

/**
 * Green when it is fine, red when it is not, grey when nobody has looked.
 *
 * "Not checked" is deliberately not green. A song nothing has read is an
 * unknown, and colouring it the same as one Gemini cleared would be the single
 * most misleading thing this screen could do.
 */
const tone = (r) => {
  if (r.score === null || r.score === undefined) return 'grey'
  return r.score >= 90 && !r.open_flags && !r.publish_blocked ? 'good' : 'bad'
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'problems', label: 'Problems' },
  { key: 'human', label: 'With a human' },
  { key: 'unchecked', label: 'Not checked' },
]

const matches = (r, f) =>
  f === 'all' ? true
  : f === 'problems' ? (r.open_flags > 0 || r.publish_blocked)
  : f === 'human' ? r.awaiting_human
  : r.score === null

/**
 * The report. Every finding on one song, the model's own reason for each, and
 * the recording itself so a decision can be made on the evidence rather than on
 * a number.
 */
function Report({ row, onDecided }) {
  const { user } = useSession()
  const [flags, setFlags] = useState(null)
  const [src, setSrc] = useState(null)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState(null)

  const [cflags, setCflags] = useState(null)
  const [cNote, setCNote] = useState('')
  const [cBusy, setCBusy] = useState(null)
  const [cErr, setCErr] = useState(null)

  useEffect(() => {
    supabase.from('content_flags')
      .select('id, source, severity, status, score, detail, report_reason, created_at, resolved_at, resolution_note')
      .eq('song_id', row.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) setErr(readableError(error))
        setFlags(data ?? [])
      })
  }, [row.id])

  const loadCopyright = useCallback(() => {
    supabase.from('copyright_flags')
      .select('id, source, severity, status, score, detail, created_at, resolved_at, resolution_note, copyright_watchlist:matched_watchlist_id(title, artist, publisher, ccli_number), matched_song:matched_song_id(title)')
      .eq('song_id', row.id)
      .order('created_at', { ascending: false })
      .then(({ data, error }) => {
        if (error) setCErr(readableError(error))
        setCflags(data ?? [])
      })
  }, [row.id])

  useEffect(() => { loadCopyright() }, [loadCopyright])

  // Who may listen is an RLS question the edge function asks on our behalf, and
  // a superadmin passes it through "superadmin reads all songs". Nothing
  // special is needed here beyond having the seat.
  useEffect(() => {
    if (!row.has_media) return
    supabase.functions.invoke('r2-playback-url', { body: { song_id: row.id } })
      .then(({ data }) => setSrc(data?.url ?? null))
  }, [row.id, row.has_media])

  const decide = async (action) => {
    setBusy(action); setErr(null)
    const { error } = await supabase.rpc('moderation_decide', {
      p_song: row.id, p_action: action, p_note: note || null,
    })
    setBusy(null)
    if (error) return setErr(readableError(error))
    setNote('')
    onDecided()
  }

  const resolveCopyright = async (id, status) => {
    setCBusy(`${id}-${status}`); setCErr(null)
    const { error } = await supabase.from('copyright_flags')
      .update({
        status, resolved_by: user?.id, resolved_at: new Date().toISOString(),
        resolution_note: cNote || null,
      })
      .eq('id', id)
    setCBusy(null)
    if (error) return setCErr(readableError(error))
    setCNote('')
    loadCopyright()
    onDecided()
  }

  // The reasons worth reading first: what is still outstanding. Resolved
  // findings stay below in the history rather than competing for attention.
  const live = (flags ?? []).filter((f) => ['open', 'reviewing'].includes(f.status))
  const past = (flags ?? []).filter((f) => !['open', 'reviewing'].includes(f.status))

  const cLive = (cflags ?? []).filter((f) => f.status === 'open')
  const cPast = (cflags ?? []).filter((f) => f.status !== 'open')

  return (
    <div className="report">
      {flags === null ? <p className="m-small">Loading the report…</p> : (
        <>
          <h4 className="m-h4">Why it was flagged</h4>
          {live.length === 0 && (
            <p className="m-small">
              Nothing is outstanding on this song
              {past.length ? ' — every finding below has been decided.' : '. No check has objected to it.'}
            </p>
          )}
          {live.map((f) => (
            <div key={f.id} className={`finding ${f.severity}`}>
              <div className="finding-head">
                <b>{SOURCE[f.source] ?? f.source}</b>
                <span className="m-small">
                  {SEVERITY[f.severity] ?? f.severity} · {STATUS[f.status] ?? f.status}
                  {f.score !== null && f.score !== undefined && ` · ${Math.round(f.score * 100)}% sure`}
                  {' · '}{ago(f.created_at)}
                </span>
              </div>
              {f.report_reason && <p className="m-small">Reason given: {f.report_reason.replace(/_/g, ' ')}</p>}
              {f.detail && <p className="finding-why">{f.detail}</p>}
            </div>
          ))}

          {row.has_media ? (
            <>
              <h4 className="m-h4">Watch or listen</h4>
              <Player src={src} mime={row.media_mime} filename={row.title} />
            </>
          ) : (
            <p className="m-small" style={{ marginTop: 14 }}>
              No recording on this song, so there is nothing for a person to watch.
            </p>
          )}

          <h4 className="m-h4">Your decision</h4>
          <input className="m-note" value={note} maxLength={500}
                 placeholder="Note (optional) — kept with the decision"
                 onChange={(e) => setNote(e.target.value)} />
          <div className="m-actions">
            <button className="m-btn" disabled={!!busy} onClick={() => decide('listen')}>
              {busy === 'listen' ? 'Sending…' : 'Needs a person to watch it'}
            </button>
            <button className="m-btn good" disabled={!!busy} onClick={() => decide('clear')}>
              {busy === 'clear' ? 'Clearing…' : 'No problem — clear it'}
            </button>
            <button className="m-btn bad" disabled={!!busy} onClick={() => decide('uphold')}>
              {busy === 'uphold' ? 'Saving…' : 'Confirm the problem'}
            </button>
          </div>
          <p className="m-small">
            Confirming keeps the song from being published and is recorded against it. Clearing
            resolves every open finding and returns the score to 100.
          </p>

          {past.length > 0 && (
            <>
              <h4 className="m-h4">Everything on record</h4>
              {past.map((f) => (
                <div key={f.id} className={`finding ${f.severity} done`}>
                  <div className="finding-head">
                    <b>{SOURCE[f.source] ?? f.source}</b>
                    <span className="m-small">
                      {SEVERITY[f.severity] ?? f.severity} · {STATUS[f.status] ?? f.status} · {ago(f.created_at)}
                    </span>
                  </div>
                  {f.detail && <p className="finding-why">{f.detail}</p>}
                  {f.resolution_note && <p className="m-small">Your note: {f.resolution_note}</p>}
                </div>
              ))}
            </>
          )}

          <h4 className="m-h4">Copyright check</h4>
          {cflags === null ? <p className="m-small">Loading…</p> : (
            <>
              {cLive.length === 0 && (
                <p className="m-small">
                  No copyright match on this song
                  {cPast.length ? ' — every match below has been decided.' : '.'}
                </p>
              )}
              {cLive.map((f) => (
                <div key={f.id} className={`finding ${f.severity}`}>
                  <div className="finding-head">
                    <b>{CSOURCE[f.source] ?? f.source}</b>
                    <span className="m-small">
                      {CSEVERITY[f.severity] ?? f.severity} · {CSTATUS[f.status] ?? f.status}
                      {f.score !== null && f.score !== undefined && ` · ${Math.round(f.score * 100)}% match`}
                      {' · '}{ago(f.created_at)}
                    </span>
                  </div>
                  {f.detail && <p className="finding-why">{f.detail}</p>}
                  {f.copyright_watchlist && (
                    <p className="m-small">
                      Matches watchlist: {f.copyright_watchlist.title}
                      {f.copyright_watchlist.artist && ` — ${f.copyright_watchlist.artist}`}
                      {f.copyright_watchlist.publisher && ` (${f.copyright_watchlist.publisher})`}
                    </p>
                  )}
                  {f.matched_song && <p className="m-small">Matches another song here: {f.matched_song.title}</p>}
                  <div className="m-actions">
                    <button className="m-btn good" disabled={!!cBusy} onClick={() => resolveCopyright(f.id, 'cleared')}>
                      {cBusy === `${f.id}-cleared` ? 'Clearing…' : 'Clear'}
                    </button>
                    <button className="m-btn bad" disabled={!!cBusy} onClick={() => resolveCopyright(f.id, 'conflict')}>
                      {cBusy === `${f.id}-conflict` ? 'Saving…' : 'Confirm conflict'}
                    </button>
                  </div>
                </div>
              ))}
              {cLive.length > 0 && (
                <input className="m-note" value={cNote} maxLength={500}
                       placeholder="Note (optional) — kept with the decision"
                       onChange={(e) => setCNote(e.target.value)} />
              )}

              {cPast.length > 0 && (
                <>
                  <h4 className="m-h4">Copyright record</h4>
                  {cPast.map((f) => (
                    <div key={f.id} className={`finding ${f.severity} done`}>
                      <div className="finding-head">
                        <b>{CSOURCE[f.source] ?? f.source}</b>
                        <span className="m-small">
                          {CSEVERITY[f.severity] ?? f.severity} · {CSTATUS[f.status] ?? f.status} · {ago(f.created_at)}
                        </span>
                      </div>
                      {f.detail && <p className="finding-why">{f.detail}</p>}
                      {f.resolution_note && <p className="m-small">Your note: {f.resolution_note}</p>}
                    </div>
                  ))}
                </>
              )}
              {cErr && <p className="err">{cErr}</p>}
            </>
          )}
        </>
      )}
      {err && <p className="err">{err}</p>}
    </div>
  )
}

export default function Moderation() {
  const [rows, setRows] = useState(null)
  const [totals, setTotals] = useState(null)
  const [filter, setFilter] = useState('all')
  const [open, setOpen] = useState(null)
  const [err, setErr] = useState(null)
  const [copyrightSongs, setCopyrightSongs] = useState(new Set())

  // Plain white, black text, two colours. Scoped to the body for the life of
  // this screen rather than written into the components, so the moderation
  // tool can be stark without the rest of Beseter losing its warmth — and so
  // night mode, which is right for a writer at 11pm and wrong for reading
  // evidence, is overridden here and nowhere else.
  useEffect(() => {
    document.body.classList.add('modplain')
    return () => document.body.classList.remove('modplain')
  }, [])

  const load = useCallback(async () => {
    setErr(null)
    const [list, sum, cf] = await Promise.all([
      supabase.from('moderation_dashboard')
        .select('*')
        // Worst first, and an unchecked song sorts to the bottom rather than in
        // with the disasters — it is a gap, not a finding.
        .order('score', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(500),
      supabase.from('moderation_overview').select('*').maybeSingle(),
      supabase.from('copyright_flags').select('song_id').eq('status', 'open').limit(500),
    ])
    if (list.error) setErr(readableError(list.error))
    setRows(list.data ?? [])
    setTotals(sum.data ?? null)
    setCopyrightSongs(new Set((cf.data ?? []).map((x) => x.song_id)))
  }, [])

  useEffect(() => { load() }, [load])

  if (rows === null) return <Shell><Spinner /></Shell>

  const shown = rows.filter((r) => matches(r, filter))
  const attention = totals ? totals.watch + totals.trouble : 0

  return (
    <Shell>
      <h2 className="m-h1">Moderation</h2>
      {totals && (
        <p className="m-lede">
          <b>{totals.songs}</b> songs · <b className={attention ? 'bad' : 'good'}>{attention}</b> need
          attention · <b>{totals.awaiting_human}</b> with a person · <b>{totals.unchecked}</b> not
          checked{totals.average_score !== null && <> · average <b>{totals.average_score}</b>/100</>}
        </p>
      )}

      <div className="m-filters">
        {FILTERS.map((f) => (
          <button key={f.key} className={`m-filter${filter === f.key ? ' on' : ''}`}
                  onClick={() => setFilter(f.key)}>
            {f.label} ({rows.filter((r) => matches(r, f.key)).length})
          </button>
        ))}
      </div>

      {shown.map((r) => {
        const t = tone(r)
        return (
          <div key={r.id} className="m-row">
            <div className="m-rowtop">
              <span className={`m-score ${t}`}>{r.score === null ? '—' : r.score}</span>
              <div className="m-who">
                <b>{r.title || 'Untitled'}</b>
                <small>
                  {[r.writer || r.writer_email, r.church, r.status?.replace(/_/g, ' ')]
                    .filter(Boolean).join(' · ')}
                  {r.submitted_at ? ` · sent ${ago(r.submitted_at)}` : ' · not sent yet'}
                </small>
              </div>
              <button className="m-view" onClick={() => setOpen(open === r.id ? null : r.id)}>
                {open === r.id ? 'Close' : 'Report'}
              </button>
            </div>

            <div className="m-tags">
              {r.publish_blocked && <span className="m-tag bad">Blocked from publishing</span>}
              {r.awaiting_human && <span className="m-tag">Waiting on a person</span>}
              {r.reports > 0 && <span className="m-tag bad">Reported by {r.reports}</span>}
              {copyrightSongs.has(r.id) && <span className="m-tag bad">Possible copyright match</span>}
              {r.score === null && <span className="m-tag">No check has run</span>}
              {r.has_media && <span className="m-tag">{r.media_mime?.startsWith('video/') ? 'Video' : 'Audio'}</span>}
              {r.music_confidence !== null && r.music_confidence !== undefined && (
                <span className="m-tag">{Math.round(r.music_confidence * 100)}% musical</span>
              )}
            </div>

            {/* The model's own sentence, on the row, so the list answers "why"
                without anybody having to open anything. */}
            {r.last_detail && open !== r.id && (
              <p className="m-why"><b>{SOURCE[r.last_source] ?? r.last_source}:</b> {r.last_detail}</p>
            )}

            {open === r.id && <Report row={r} onDecided={load} />}
          </div>
        )
      })}

      <p className="m-small" style={{ marginTop: 18 }}>
        {rows.length === 0
          ? 'No songs yet. Every submission will land here with a score.'
          : shown.length === 0 ? 'Nothing in this filter.' : `${shown.length} shown.`}
      </p>
      {err && <p className="err">{err}</p>}
    </Shell>
  )
}
